import request from "../utils/request";

export type RagSyncTargetType = "SourcePathAsk" | "SourcePathStudy" | "DocCategory" | "DocTag";
export type RagSyncEvent = "Create" | "Updated" | "Delete";
export type RagSyncResult = "Succeeded" | "Failed";
export type RagBulkSyncTarget = "categories" | "tags" | "sources";

export interface RagSyncLog {
  id: string;
  targetType: RagSyncTargetType;
  targetId: string;
  syncEvent: RagSyncEvent;
  /** 北京时间 yyyy-MM-dd HH:mm:ss */
  syncTime: string;
  syncResult: RagSyncResult;
  apiUrl: string | null;
  syncRequest: string | null;
  syncResp: string | null;
}

export interface RagSyncLogFilters {
  targetType?: RagSyncTargetType | "";
  syncEvent?: RagSyncEvent | "";
  syncResult?: RagSyncResult | "";
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RagSyncLogPage {
  data: RagSyncLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RagBulkSyncResult {
  targetType: string;
  total: number;
  succeeded: number;
  failed: number;
  failedTargetIds: string[];
}

const NO_REQUEST_TIMEOUT = { timeout: 0 } as const;

export async function getRagSyncLogs(filters: RagSyncLogFilters = {}): Promise<RagSyncLogPage> {
  const params: Record<string, string> = {};
  if (filters.targetType) params.targetType = filters.targetType;
  if (filters.syncEvent) params.syncEvent = filters.syncEvent;
  if (filters.syncResult) params.syncResult = filters.syncResult;
  if (filters.search?.trim()) params.search = filters.search.trim();
  if (filters.page) params.page = String(filters.page);
  if (filters.pageSize) params.pageSize = String(filters.pageSize);

  const response = await request.get<RagSyncLogPage>("/project/ragsynclog/list", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return (
    response.data ?? {
      data: [],
      total: 0,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
    }
  );
}

export async function retryRagSyncLog(id: string): Promise<RagSyncLog> {
  const response = await request.post<RagSyncLog>(
    `/project/ragsynclog/${encodeURIComponent(id)}/retry`,
    undefined,
    NO_REQUEST_TIMEOUT,
  );
  return response.data;
}

export async function syncAllRagTargets(target: RagBulkSyncTarget): Promise<RagBulkSyncResult> {
  const response = await request.post<RagBulkSyncResult>(
    `/project/ragsynclog/sync/${target}`,
    undefined,
    NO_REQUEST_TIMEOUT,
  );
  return (
    response.data ?? {
      targetType: target,
      total: 0,
      succeeded: 0,
      failed: 0,
      failedTargetIds: [],
    }
  );
}

export function getRagSyncErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (typeof data === "string" && data.trim()) return data;
  return error instanceof Error && error.message ? error.message : fallback;
}
